import type { LuggageServiceConfigurations } from '@/cms/fields/types/checkout-page'
import { useIntl } from '@/intl'
import CheckoutSection from '@/scopes/checkout/components/CheckoutSection'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'

import { ParticipantName, RoomName } from '@/scopes/checkout/components/services/ServiceComponents'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useCheckoutSid, useService } from '@/scopes/checkout/domain/hooks'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

import { BulletList, Button, ChoiceItem, Dropdown, DropdownOption, FormField, FormFieldControl, FormFieldLabel, ListItem, SpaceBetween, Switch, tk, Typography } from '@sunwebgroup/ui-components'

import { Banner, type BannerColors, Icon, Tag } from '@sunwebgroup/ui-components/uds'
import { Typography as TypographyUDS } from '@sunwebgroup/ui-components/uds'
import { isEmpty, values } from 'ramda'
import * as React from 'react'
import { useSessionStorage } from 'react-use'
import { css, styled } from 'styled-components'
import {
  updateLuggageService,
  updateOneClickLuggageService,
  updateSpecialLuggageService,
} from '../../domain/actions'
import { createAncillariesClickedEvent } from '../../domain/analytics'
import { bannerColorMap, BannerType, type CheckoutServiceOption, ServiceType } from '../../domain/types'

const ParticipantDropdowns = styled.div`
  display: flex;
  align-items: center;
  min-width: 100%;
  gap: ${tk('spacing_md')};
  flex-direction: column;
  @media ${tk('mq_desktop_sm')} {
    flex-direction: row;
    justify-content: space-between;
  }
`
const ParticipantDropdown = styled(FormField)`
  width: 100%;
`
const TotalParticipantAmount = styled(Typography)`
  @media ${tk('mq_desktop_sm')} {
    min-width: 100px;
    text-align: right;
    padding-top: ${tk('spacing_sm')};
    height: 52px;
    align-self: flex-end;
  }
`
const FlightsInformationText = styled.div`
  display: flex;
  gap: ${tk('spacing_md')};
  justify-content: space-evenly;
  flex-direction: column;
  flex-wrap: nowrap;
  min-width: auto;
  @media ${tk('mq_desktop_sm')} {
    flex-direction: row;
    gap: ${tk('spacing_lg')};
    align-items: flex-end;
  }
`
const DividerLine = styled.div`
  border-top: 1px solid ${tk('color_semantic_border')};
`
const SwitchLabel = styled(ChoiceItem.Label)`
  color: ${tk('color_semantic_text')};
`
const OneClickLuggageContent = styled(SpaceBetween)`
  width: 100%;
`
const OneClickLuggageLabel = styled(Typography)`
  color: ${tk('color_semantic_text')};
`
const OneClickLuggageBulletListItem = styled(BulletList.Item)`
  color: ${tk('color_semantic_text_strong')};
`
const OneClickLuggagePriceDetail = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
`
const OneClickLuggageOptions = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${tk('spacing_md')};
  margin-bottom: ${tk('spacing_md')};
  flex: 0 0 100%;
  overflow-x: auto;
  padding-bottom: ${tk('spacing_xs')};
  margin-left: -${tk('spacing_md')};
  margin-right: -${tk('spacing_md')};
  padding-left: ${tk('spacing_md')};
  padding-right: ${tk('spacing_md')};
`
const OneClickLuggageOption = styled.div<{ $selected?: boolean }>`
 flex: 1 1 25%;
  ${({ $selected }) => $selected && modSelectedOneClickLuggage()}
`
function modSelectedOneClickLuggage() {
  return css`
  ${ListItem.Root} {
    border-color: ${tk('color_semantic_border_strong_active')};
  }
`
}
const OneClickLuggageButton = styled(Button)`
  width: 100%;
`

const maxOneClickOptionsToBeShown = 3

export function LuggageService({ ...configurations }: LuggageServiceConfigurations) {
  const intl = useIntl()
  const sid = useCheckoutSid()
  const { sendEvent } = useAnalytics()
  const { showSpecialLuggage } = configurations

  const { service, setServiceValue } = useService(ServiceType.Luggage)
  useSendAncillaryAvailable(service, 'selectLuggage')

  const [selectPerParticipantChecked, setSelectPerParticipantChecked] = useSessionStorage(
    `${sid}-luggage-is-per-participant`,
    !service?.isOneClickLuggageReady,
  )

  const switchLuggageVisualisation = React.useCallback(
    (value: boolean) => {
      setSelectPerParticipantChecked(value)
      sendEvent(
        createAncillariesClickedEvent({
          description: 'Change luggage visualization',
          element: 'toggle',
          type: 'Luggage',
          result: !selectPerParticipantChecked ? 'element selected' : 'element unselected',
        }),
      )
    },
    [sendEvent, setSelectPerParticipantChecked, selectPerParticipantChecked],
  )

  const handleSpecialLuggage = React.useCallback(
    (value: boolean) => {
      if (!service)
        return
      setServiceValue(updateSpecialLuggageService({ value })(service))
      sendEvent(
        createAncillariesClickedEvent({
          description: 'Set special luggage',
          element: 'toggle',
          type: 'Special Luggage',
          result: value ? 'element selected' : 'element unselected',
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  if (!service)
    return null
  const { hasSpecialLuggage } = service

  return (
    <CheckoutSection
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_description' })}
      glyph={getIconForService(ServiceType.Luggage)}
      moreInformationTitle={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_modal_title' })}
      moreInformationDescription={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_modal_description' })}
    >
      <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
        {service.isOneClickLuggageReady && (
          <>
            <Typography $variant="body-md-bold">{service.flightAirports}</Typography>
            <LuggageServiceOneClick />
            <ChoiceItem.Root>
              <ChoiceItem.Control>
                <Switch checked={selectPerParticipantChecked} onCheckedChange={switchLuggageVisualisation} />
              </ChoiceItem.Control>
              <SwitchLabel>
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_applies_to_all_participants' })}
              </SwitchLabel>
            </ChoiceItem.Root>
          </>
        )}
        {selectPerParticipantChecked && <LuggageServicePerParticipant /> }
        {showSpecialLuggage && (
          <ChoiceItem.Root>
            <ChoiceItem.Control>
              <Switch defaultChecked={hasSpecialLuggage} onCheckedChange={handleSpecialLuggage} />
            </ChoiceItem.Control>
            <SwitchLabel>{intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_set_special_luggage' })}</SwitchLabel>
            { hasSpecialLuggage && <ChoiceItem.SupportingText>{intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_set_special_luggage_extra_text' })}</ChoiceItem.SupportingText> }
          </ChoiceItem.Root>
        )}
      </SpaceBetween>
    </CheckoutSection>
  )
}

function LuggageServicePerParticipant() {
  const { service } = useService(ServiceType.Luggage)

  if (!service)
    return null
  const { outboundOptions, inboundOptions, rooms, participants, banners } = service
  const uniqueBanners = Array.from(
    new Map(
      banners.map(banner => [banner.title, banner]),
    ).values(),
  )
  return (
    <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
      <FlightsInformation />
      {(values(outboundOptions).length > 0 || values(inboundOptions).length > 0) && (
        rooms.map(({ id: roomId, name: roomName, participantIds }) => (
          <SpaceBetween key={roomId} direction="y" spacing={tk('spacing_md')}>
            {rooms.length > 1 && (
              <RoomName as="p" $variant="body-sm-bold">
                {roomName}
              </RoomName>
            )}
            {participantIds.map((participantId) => {
              const participant = participants?.[participantId]
              if (!participant || participant.availableOptions.length === 0) {
                return null
              }

              return (
                <ParticipantRow
                  {...participant}
                  key={participantId}
                  outboundOptions={outboundOptions}
                  inboundOptions={inboundOptions}
                />
              )
            })}
          </SpaceBetween>
        ))
      )}
      {(values(outboundOptions).length > 0 || values(inboundOptions).length === 0) && (
        isEmpty(rooms)
        && Object.values(participants ?? {}).map(participant => (
          <ParticipantRow {...participant} key={participant.id} outboundOptions={outboundOptions} inboundOptions={inboundOptions} />
        ))
      )}
      { uniqueBanners.map(({ title, text, type = 'info' }, index) => (
        <Banner
          key={`banner-${index}`}
          color={bannerColorMap[type as BannerType] as BannerColors}
          text={title ?? ''}
          size={'M' as any}
          supportingText={text}
        />
      ))}
    </SpaceBetween>
  )
}

function LuggageServiceOneClick() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { service, setServiceValue } = useService(ServiceType.Luggage)

  const handleOneClickLuggageChange = React.useCallback(
    (value: string | null) => {
      if (!service)
        return
      setServiceValue(updateOneClickLuggageService({ value })(service))

      const optionClicked = service.oneClickLuggageOptions[value ?? '']
      sendEvent(
        createAncillariesClickedEvent({
          description: 'selectOneClickLuggage',
          element: 'button',
          type: optionClicked?.label || '',
          result: optionClicked?.price || 0,
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  if (!service)
    return null

  const { oneClickLuggageOptions, oneClickSelectedValue } = service

  return (
    <OneClickLuggageOptions>
      {Object.values(oneClickLuggageOptions).slice(0, maxOneClickOptionsToBeShown).map((option) => {
        const { value, label, price, badge } = option

        const isSelected = value === oneClickSelectedValue

        return (
          <OneClickLuggageOption key={value} $selected={isSelected ? true : undefined}>
            <ListItem.Root as="div">
              <ListItem.Content>
                <OneClickLuggageContent direction="y" spacing={tk('spacing_xs')}>
                  {badge && <Tag variant="success-tinted" size="M">{badge}</Tag>}
                  <ListItem.Label><OneClickLuggageLabel $variant="body-md-bold">{label}</OneClickLuggageLabel></ListItem.Label>
                  <BulletList.Root $type="vertical">
                    <OneClickLuggageBulletListItem>
                      <Typography $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_one_click_handbagage_inclusief' })}</Typography>
                    </OneClickLuggageBulletListItem>
                  </BulletList.Root>
                  <SpaceBetween direction="x" spacing={tk('spacing_xs')} alignItems="center">
                    <OneClickLuggagePriceDetail as="span" $variant="body-sm">{intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_one_click_price_label' })}</OneClickLuggagePriceDetail>
                    <Typography as="span" $variant="label">
                      {intl.formatCurrency(price, price < 0)}
                    </Typography>
                  </SpaceBetween>
                  {isSelected && (
                    <OneClickLuggageButton $hierarchy="tertiary" $withoutColor onClick={() => handleOneClickLuggageChange(null)}>
                      <Icon glyph="TrashCan" size={24} />
                      {intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_one_click_remove' })}
                    </OneClickLuggageButton>
                  )}
                  {!isSelected && (
                    <OneClickLuggageButton $hierarchy="secondary" $withoutColor onClick={() => handleOneClickLuggageChange(value)}>
                      {intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_one_click_select' })}
                    </OneClickLuggageButton>
                  )}
                </OneClickLuggageContent>
              </ListItem.Content>
            </ListItem.Root>
          </OneClickLuggageOption>
        )
      })}
    </OneClickLuggageOptions>
  )
}
function FlightsInformation() {
  const { service } = useService(ServiceType.Luggage)

  if (!service)
    return null
  const { flightsInformationBanner } = service
  const flightsInformationHtml = flightsInformationBanner
    ? (
        <>
          {flightsInformationBanner.outboundFlight || flightsInformationBanner.inboundFlight
            ? (
                <>
                  <DividerLine />
                  <FlightsInformationText>
                    {flightsInformationBanner.outboundFlight && (
                      <SpaceBetween direction="y">
                        <TypographyUDS $variant="body-m-bold-snug">{flightsInformationBanner.outboundFlight.title}</TypographyUDS>
                        <ul>
                          <li><TypographyUDS $variant="body-m-regular-snug">{flightsInformationBanner.outboundFlight.handLuggage}</TypographyUDS></li>
                          <li><TypographyUDS $variant="body-m-regular-snug">{flightsInformationBanner.outboundFlight.checkedInLuggage}</TypographyUDS></li>
                        </ul>
                      </SpaceBetween>
                    )}
                    { flightsInformationBanner.inboundFlight && (
                      <SpaceBetween direction="y">
                        <TypographyUDS $variant="body-m-bold-snug">{flightsInformationBanner.inboundFlight.title}</TypographyUDS>
                        <ul>
                          <li><TypographyUDS $variant="body-m-regular-snug">{flightsInformationBanner.inboundFlight.handLuggage}</TypographyUDS></li>
                          <li><TypographyUDS $variant="body-m-regular-snug">{flightsInformationBanner.inboundFlight.checkedInLuggage}</TypographyUDS></li>
                        </ul>
                      </SpaceBetween>
                    )}
                  </FlightsInformationText>
                </>
              )
            : null}
        </>
      )
    : ''

  return (
    <>
      {flightsInformationBanner && (
        <Banner
          color={bannerColorMap[flightsInformationBanner.type as BannerType] as BannerColors}
          text={flightsInformationBanner.title ?? ''}
          size={'M' as any}
          supportingText={flightsInformationBanner.text ?? ''}
          supportingTextHtml={flightsInformationHtml}
        />
      )}
    </>
  )
}

function ParticipantRow({
  id,
  outboundOptions,
  inboundOptions,
  availableOptions,
  selectedValueInbound,
  selectedValueOutbound,
  name,
}: {
  id: string
  outboundOptions: Record<string, CheckoutServiceOption>
  inboundOptions: Record<string, CheckoutServiceOption>
  availableOptions: string[]
  selectedValueInbound: string | null
  selectedValueOutbound: string | null
  name: string
}) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { service, setServiceValue } = useService(ServiceType.Luggage)
  const selectedOutboundOption = selectedValueOutbound ? outboundOptions[selectedValueOutbound] : undefined
  const selectedInboundOption = selectedValueInbound ? inboundOptions[selectedValueInbound] : undefined
  const [totalParticipantAmount, setTotalParticipantAmount] = React.useState((selectedOutboundOption?.price ?? 0) + (selectedInboundOption?.price ?? 0))

  const handleOutboundChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updateLuggageService({ participantId: id, value, direction: 'outbound' })(service))
      setTotalParticipantAmount((outboundOptions[value ?? '']?.price ?? 0) + (inboundOptions[selectedValueInbound ?? '']?.price ?? 0))
      sendEvent(
        createAncillariesClickedEvent({
          description: 'selectLuggage',
          element: 'dropdown',
          type: outboundOptions[value ?? '']?.label ?? '',
          result: outboundOptions[value ?? '']?.price ?? 0,
        }),
      )
    },
    [sendEvent, setServiceValue, service, setTotalParticipantAmount, id, selectedValueInbound, outboundOptions, inboundOptions],
  )

  const handleInboundChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updateLuggageService({ participantId: id, value, direction: 'inbound' })(service))
      setTotalParticipantAmount((outboundOptions[selectedValueOutbound ?? '']?.price ?? 0) + (inboundOptions[value ?? '']?.price ?? 0))
      sendEvent(
        createAncillariesClickedEvent({
          description: 'selectLuggage',
          element: 'dropdown',
          type: inboundOptions[value ?? '']?.label ?? '',
          result: inboundOptions[value ?? '']?.price ?? 0,
        }),
      )
    },
    [sendEvent, setServiceValue, service, setTotalParticipantAmount, id, selectedValueOutbound, outboundOptions, inboundOptions],
  )

  React.useEffect(() => {
    if (selectedValueOutbound) {
      setTotalParticipantAmount((outboundOptions[selectedValueOutbound]?.price ?? 0) + (inboundOptions[selectedValueInbound ?? '']?.price ?? 0))
    }
    if (selectedValueInbound) {
      setTotalParticipantAmount((outboundOptions[selectedValueOutbound ?? '']?.price ?? 0) + (inboundOptions[selectedValueInbound]?.price ?? 0))
    }
  }, [selectedValueOutbound, selectedValueInbound, inboundOptions, outboundOptions])

  if (!availableOptions.length)
    return null
  if (!outboundOptions && !inboundOptions)
    return null
  if (values(outboundOptions).length === 0 && values(inboundOptions).length === 0)
    return null

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
      <ParticipantName>
        {name}
      </ParticipantName>
      <ParticipantDropdowns>
        { Object.values(outboundOptions).length > 0 && (
          <ParticipantDropdown>
            <FormFieldLabel>
              <Typography as="span" $variant="body-xs">
                {' '}
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_outbound_label' })}
              </Typography>
            </FormFieldLabel>
            <FormFieldControl>
              <Dropdown
                placeholder={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_outbound_placeholder' })}
                disablePlaceholder
                value={selectedValueOutbound ?? null}
                onValueChange={newValue => handleOutboundChange(newValue ?? '')}
              >
                {Object.values(outboundOptions).map((option) => {
                  return (
                    <DropdownOption key={option.value} value={option.value} disabled={!availableOptions.includes(option.value)}>
                      {option.label}
                      {' '}
                      -
                      {intl.formatCurrency(option.price)}
                    </DropdownOption>
                  )
                })}
              </Dropdown>
            </FormFieldControl>
          </ParticipantDropdown>
        )}
        { Object.values(inboundOptions).length > 0 && (
          <ParticipantDropdown>
            <FormFieldLabel>
              <Typography as="span" $variant="body-xs">{intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_inbound_label' })}</Typography>
            </FormFieldLabel>
            <FormFieldControl>
              <Dropdown
                placeholder={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_inbound_placeholder' })}
                disablePlaceholder
                value={selectedValueInbound ?? null}
                onValueChange={newValue => handleInboundChange(newValue ?? '')}
              >
                {Object.values(inboundOptions).map((option) => {
                  return (
                    <DropdownOption key={option.value} value={option.value} disabled={!availableOptions.includes(option.value)}>
                      {option.label}
                      {' '}
                      -
                      {intl.formatCurrency(option.price)}
                    </DropdownOption>
                  )
                })}
              </Dropdown>
            </FormFieldControl>
          </ParticipantDropdown>
        )}

        <TotalParticipantAmount as="label" $variant="body-md">
          {intl.formatCurrency(totalParticipantAmount)}
        </TotalParticipantAmount>
      </ParticipantDropdowns>
    </SpaceBetween>
  )
}
